import type { DensityMode } from './appearance';
import type { CalendarView } from '@/features/calendar/types/calendar.types';

export type AppDensityMode = 'inherit' | DensityMode;

export type MatterViewMode = 'table' | 'card' | 'kanban';
export type DocumentViewMode = 'table' | 'grid';
export type ClientViewMode = 'table' | 'card' | 'kanban';

export type DocumentSortField = 'updated_at' | 'created_at' | 'title' | 'size' | 'type';
export type TaskSortField = 'due_date' | 'priority' | 'created_at';
export type TaskStatusFilter = 'all' | 'todo' | 'in_progress' | 'review' | 'done' | 'archived';
export type WeekStartOption = 'system' | 'sunday' | 'monday';
export type WorkingHoursMode = 'system' | 'custom';

export interface MattersPreferences {
  defaultView: MatterViewMode;
  density: AppDensityMode;
  showKpiSummary: boolean;
}

export interface DocumentsPreferences {
  defaultView: DocumentViewMode;
  defaultSort: DocumentSortField;
  density: AppDensityMode;
  showAnalytics: boolean;
}

export interface ClientsPreferences {
  defaultView: ClientViewMode;
  density: AppDensityMode;
  showKpiSummary: boolean;
}

export interface TasksPreferences {
  defaultSort: TaskSortField;
  defaultStatusFilter: TaskStatusFilter;
  density: AppDensityMode;
  showOverdueHighlight: boolean;
}

export interface CalendarPreferences {
  defaultView: CalendarView;
  weekStart: WeekStartOption;
  showWeekends: boolean;
  workingHoursMode: WorkingHoursMode;
  workingHoursStart: string;
  workingHoursEnd: string;
  density: AppDensityMode;
}

export interface AppPreferences {
  matters: MattersPreferences;
  documents: DocumentsPreferences;
  clients: ClientsPreferences;
  tasks: TasksPreferences;
  calendar: CalendarPreferences;
}

export type AppPreferencesUpdate = {
  matters?: Partial<MattersPreferences>;
  documents?: Partial<DocumentsPreferences>;
  clients?: Partial<ClientsPreferences>;
  tasks?: Partial<TasksPreferences>;
  calendar?: Partial<CalendarPreferences>;
};

export const DEFAULT_APP_PREFERENCES: AppPreferences = {
  matters: {
    defaultView: 'table',
    density: 'inherit',
    showKpiSummary: true,
  },
  documents: {
    defaultView: 'table',
    defaultSort: 'updated_at',
    density: 'inherit',
    showAnalytics: true,
  },
  clients: {
    defaultView: 'table',
    density: 'inherit',
    showKpiSummary: true,
  },
  tasks: {
    defaultSort: 'due_date',
    defaultStatusFilter: 'all',
    density: 'inherit',
    showOverdueHighlight: true,
  },
  calendar: {
    defaultView: 'month',
    weekStart: 'system',
    showWeekends: true,
    workingHoursMode: 'system',
    workingHoursStart: '08:00',
    workingHoursEnd: '18:00',
    density: 'inherit',
  },
};
